// frontend/src/components/PipelineMonitor/PipelineMonitor.tsx

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePipelineMonitor } from '../../hooks/usePipelineMonitor';
import { HistoricoExecucoes } from './HistoricoExecucoes';
import { ExecucaoDetalhadaComponent } from './ExecucaoDetalhada';
import './PipelineMonitor.css';

export const PipelineMonitor: React.FC = () => {
  const { user, isAuthenticated, loading: authLoading, selectedTraceId, selectTrace } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [execucaoSelecionada, setExecucaoSelecionada] = useState<string | null>(null);

  // Guarda o traceId que já foi disparado, pra não disparar 2x
  const traceJaDisparadoRef = useRef<string | null>(null);

  const {
    historico,
    execucaoAtiva,
    traceIdAtivo,
    monitorando,
    carregandoHistorico,
    carregandoExecucao,
    erro,
    iniciarMonitoramento,
    pararMonitoramento,
  } = usePipelineMonitor(user?.id ?? null);

  // ============================================================
  // SELEÇÃO DE EXECUÇÃO — prioridade única, sem concorrência
  // 1. trace vindo do redirect (/orc → /monitor) — PRIORIDADE MÁXIMA
  // 2. selectedTraceId do AuthContext (seleção global em /datasets)
  // 3. trace mais recente do histórico
  // ============================================================
  useEffect(() => {
    const traceIdFromState = (location.state as any)?.traceId;
    const datasetNameFromState = (location.state as any)?.datasetName;

    // -------- Caso 1: veio do redirect do ORC --------
    if (traceIdFromState) {
      if (traceJaDisparadoRef.current !== traceIdFromState) {
        traceJaDisparadoRef.current = traceIdFromState;
        setExecucaoSelecionada(traceIdFromState);
        
        // Se veio do ORC, atualiza o AuthContext com o nome real do dataset
        const datasetName = datasetNameFromState || 'Dataset';
        selectTrace(traceIdFromState, datasetName);

        iniciarMonitoramento(traceIdFromState);
      }
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    // -------- Caso 2: selectedTraceId do AuthContext --------
    if (selectedTraceId) {
      if (traceJaDisparadoRef.current !== selectedTraceId) {
        traceJaDisparadoRef.current = selectedTraceId;
        setExecucaoSelecionada(selectedTraceId);
        iniciarMonitoramento(selectedTraceId);
      }
      return;
    }

    // -------- Caso 3: sem redirect e sem selectedTraceId --------
    if (traceJaDisparadoRef.current) return;
    if (historico.length === 0) return;

    const emAndamento = historico.find(
      h => h.status === 'STARTED' || h.status === 'RUNNING'
    );
    
    const alvo = emAndamento ?? historico[0];

    traceJaDisparadoRef.current = alvo.trace_id;
    setExecucaoSelecionada(alvo.trace_id);
    iniciarMonitoramento(alvo.trace_id);
  }, [
    location.state,
    location.pathname,
    navigate,
    historico,
    iniciarMonitoramento,
    selectedTraceId,
    selectTrace,
  ]);

  const handleSelecionarExecucao = (traceId: string) => {
    traceJaDisparadoRef.current = traceId;
    setExecucaoSelecionada(traceId);
    iniciarMonitoramento(traceId);
  };

  const handlePararMonitoramento = () => {
    traceJaDisparadoRef.current = null;
    pararMonitoramento();
    setExecucaoSelecionada(null);
  };

  if (authLoading) {
    return (
      <div className="page-container">
        <div className="loading-screen">Carregando...</div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="page-container">
        <div className="login-box">
          <h1>📊 Monitor de Pipelines</h1>
          <h2>🔒 Faça login para acessar</h2>
          <p style={{ color: '#8899aa', marginTop: '16px' }}>
            Você precisa estar logado para visualizar suas execuções.
          </p>
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="page-container">
        <div
          className="error-banner"
          style={{ padding: '20px', color: '#ef5350' }}
        >
          ❌ {erro}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="header-monitor">
        <h1>📊 Monitor de Pipelines</h1>
        <div className="user-info">
          <span>👤 {user.name || user.email}</span>
        </div>
      </div>

      <div className="two-columns">
        <div className="column-left">
          <HistoricoExecucoes
            historico={historico}
            carregando={carregandoHistorico}
            onSelecionarTrace={handleSelecionarExecucao}
            traceIdAtivo={execucaoSelecionada}
          />
        </div>

        <div className="column-right">
          {execucaoAtiva.length > 0 ? (
            <ExecucaoDetalhadaComponent
              execucao={execucaoAtiva}
              onParar={handlePararMonitoramento}
              carregando={carregandoExecucao}
            />
          ) : traceIdAtivo ? (
            <div className="empty-state">
              <span className="empty-icon">⏳</span>
              <h3>Aguardando primeira etapa...</h3>
              <p>O pipeline foi disparado. Assim que a primeira etapa começar, os dados aparecerão aqui.</p>
              <p className="hint">Atualizando automaticamente a cada 3 segundos</p>
            </div>
          ) : (
            <div className="empty-state">
              <span className="empty-icon">📊</span>
              <h3>Nenhuma execução selecionada</h3>
              <p>Clique em uma execução no histórico para ver os detalhes</p>
              <p className="hint">
                As execuções em andamento são atualizadas automaticamente
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PipelineMonitor;